import React, { useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Col,
  Form,
  Input,
  Progress,
  Row,
  Upload,
  Typography,
} from "antd";
import {
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import { Dashboard } from "@uppy/react";
import axios from "axios";
import CustomRichTextEditor from "../../../components/CustomTextEditor";

import {
  ADD_VIDEO_VIEW,
  GET_AWS_SIGN_UPLOAD_VIDEO,
} from "../../../apis/apis";

const { Text } = Typography;

function AddVideo(props) {
  const { notification, message } = App.useApp();
  const { set_add_video, list_refresh, set_list_refresh } = props;

  const [form] = Form.useForm();

  const [errors, set_errors] = useState({});

  const [title, set_title] = useState("");
  const [description, set_description] = useState("");

  const [thumbnail, set_thumbnail] = useState("");
  const [thumbnail_api, set_thumbnail_api] = useState(null);
  const [thumbnailError, setthumbnailError] = useState("");

  const [thumbnailLoading, setThumbnailLoading] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [video_uploading_message, set_video_uploading_message] = useState("");

  const [organization_video, set_organization_video] = useState("");
  const [total_video_length, set_total_video_length] = useState(0);

  const [upload_button_enable, set_upload_button_enable] = useState(false);

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };
  const isFormValid = useMemo(() => {
    return (
      title.trim() &&
      description.trim() &&
      thumbnail_api &&
      organization_video &&
      upload_button_enable
    );
  }, [
    title,
    description,
    thumbnail_api,
    organization_video,
    upload_button_enable,
  ]);
  const getVideoDuration = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");

      video.preload = "metadata";

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };

      video.onerror = () => {
        reject(new Error("Invalid video file"));
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const validateThumbnail = async (file) => {
    const isJpgOrPng =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/jpg";

    if (!isJpgOrPng) {
      set_thumbnail_api(null);
      set_thumbnail("");
      setthumbnailError("Only JPG/PNG files are allowed.");
      return Upload.LIST_IGNORE;
    }

    const isLt512KB = file.size <= 512 * 1024;

    if (!isLt512KB) {
      set_thumbnail_api(null);
      set_thumbnail("");
      setthumbnailError(
        "Thumbnail must be smaller than or equal to 512KB."
      );
      return Upload.LIST_IGNORE;
    }

    const image = new Image();

    const imageValidationPromise = new Promise((resolve) => {
      image.onload = async () => {
        const { width, height } = image;

        if (width >= 490 && height >= 320) {
          setthumbnailError("");
          set_thumbnail_api(file);

          const preview = await getBase64(file);
          set_thumbnail(preview);

          resolve(false);
        } else {
          set_thumbnail_api(null);
          set_thumbnail("");
          setthumbnailError(
            "Image must be at least 490x320 pixels."
          );

          resolve(Upload.LIST_IGNORE);
        }
      };
    });

    image.src = URL.createObjectURL(file);

    return imageValidationPromise;
  };

  const uppy = useMemo(() => {
    const uppyInstance = new Uppy({
      autoProceed: true,
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: ["video/mp4"],
        maxFileSize: 3 * 1024 * 1024 * 1024,
      },
    });

    uppyInstance.use(AwsS3, {
      timeout:0,
      async getUploadParameters(file) {
        try {
          setUploading(true);
          setProgress(0);
          set_video_uploading_message("Preparing upload...");

          const duration = await getVideoDuration(file.data);
          set_total_video_length(duration);

          const formData = new FormData();
          formData.append("filename", file.name);
          formData.append("type", file.type);

          const response = await GET_AWS_SIGN_UPLOAD_VIDEO(formData);

          if (!response?.data?.status) {
            throw new Error("Failed to generate upload URL");
          }

          const uploadUrl = response?.data?.uploadUrl;
          const fileKey = response?.data?.fileKey;

          set_organization_video(fileKey);

          return {
            method: "PUT",
            url: uploadUrl,
            fields: {},
            headers: {
              "Content-Type": file.type,
            },
          };
        } catch (error) {
          console.error(error);

          notification.error({
            message: "Upload Failed",
            description:
              error?.message || "Unable to prepare upload.",
          });

          throw error;
        }
      },
    });

    uppyInstance.on(
      "upload-progress",
      (file, progressData) => {
        const percent = Math.round(
          (progressData.bytesUploaded /
            progressData.bytesTotal) *
          100
        );

        if (percent >= 100) {
          set_video_uploading_message(
            "Uploading and verifying..."
          );
        } else {
          set_video_uploading_message("Uploading video...");
        }

        setProgress(percent >= 100 ? 99 : percent);
      }
    );

    uppyInstance.on("upload-success", () => {
      setProgress(100);
      setUploading(false);


      set_video_uploading_message(
        "Upload successful & verified"
      );

      notification.success({
        message: "Upload Successful",
        description: "Video uploaded successfully.",
      });
      setTimeout(() => {
        set_upload_button_enable(true);
      }, 5000);
    });

    uppyInstance.on("upload-error", (file, error) => {
      console.error(file, error);

      setUploading(false);
      setProgress(0);
      set_upload_button_enable(false);

      notification.error({
        message: "Upload Failed",
        description:
          error?.message || "Something went wrong.",
      });
    });

    return uppyInstance;
  }, []);

  // useEffect(() => {
  //   return () => {
  //     if (uppy) {
  //       uppy.close();
  //     }
  //   };
  // }, [uppy]);
  const onFinish = async () => {
    try {
      if (!thumbnail_api) {
        message.error("Please upload thumbnail");
        return;
      }

      if (!organization_video) {
        message.error("Please upload video");
        return;
      }

      set_upload_button_enable(false);

      const formData = new FormData();

      formData.append("title", title);
      formData.append("thumbnail", thumbnail_api);
      formData.append("description", description);
      formData.append("organization_video", organization_video);
      formData.append("total_video_length", total_video_length);

      const response = await ADD_VIDEO_VIEW(formData);

      if (response?.data?.status) {
        notification.success({
          message: "Successful",
          description: "Video saved successfully.",
        });

        set_add_video(false);

        list_refresh
          ? set_list_refresh(false)
          : set_list_refresh(true);
      } else {
        set_errors(response?.data?.errors || {});

        notification.error({
          message: "Validation Failed",
          description: "Please check all fields.",
        });
      }
    } catch (error) {
      console.error(error);

      notification.error({
        message: "Error",
        description: "Something went wrong.",
      });
    } finally {
      set_upload_button_enable(true);
    }
  };

  const uploadButton = (
    <button
      type="button"
      style={{
        border: 0,
        background: "none",
      }}
    >
      {thumbnailLoading ? (
        <LoadingOutlined style={{ color: "#fff" }} />
      ) : (
        <PlusOutlined style={{ color: "#fff" }} />
      )}

      <div style={{ marginTop: 8, color: "#fff" }}>
        Upload
      </div>
    </button>
  );

  return (
    <div style={{ padding: 16 }}>
      <Form
        layout="vertical"
        autoComplete="off"
        form={form}
        onFinish={onFinish}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item label="Thumbnail">
              <Upload
                listType="picture-card"
                showUploadList={false}
                beforeUpload={validateThumbnail}
                customRequest={({ onSuccess }) => {
                  setTimeout(() => {
                    onSuccess("ok");
                  }, 0);
                }}
              >
                {thumbnail ? (
                  <img
                    src={thumbnail}
                    alt="thumbnail"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>

              {thumbnailError && (
                <Text type="danger">{thumbnailError}</Text>
              )}

              {errors?.thumbnail && (
                <Text type="danger">{errors?.thumbnail}</Text>
              )}

              <p
                style={{
                  color: "#65e7c4",
                  marginTop: 10,
                }}
              >
                Note - Thumbnail must be JPG/PNG, less than 512KB and minimum
                size should be 490x320.
              </p>
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={16} lg={16}>
            <Form.Item label="Title">
              <Input
                placeholder="Enter video title"
                value={title}
                onChange={(e) => set_title(e.target.value)}
              />

              {errors?.title && (
                <Text type="danger">{errors?.title}</Text>
              )}
            </Form.Item>

            <Form.Item label="Description">
              <CustomRichTextEditor
                value={description}
                editorLabel="Description"
                onChange={(val) => set_description(val)}
                placeholder="Write something..."
              />

              {errors?.description && (
                <Text type="danger">{errors?.description}</Text>
              )}
            </Form.Item>



            <Form.Item label="Video File">
              <Dashboard
                uppy={uppy}
                key={uppy}
                proudlyDisplayPoweredByUppy={false}
                showRemoveButtonAfterComplete={false}
                height={300}
                width="100%"
              />




              {errors?.organization_video && (
                <Text type="danger">
                  {errors?.organization_video}
                </Text>
              )}
            </Form.Item>

            {progress === 100 ? (
              <>
                {isFormValid ? (
                  <Button type="primary" htmlType="submit">
                    Upload
                  </Button>
                ) : (
                  <Button type="primary" disabled>
                    Upload
                  </Button>
                )}
              </>
            ) : (
              <Button type="primary" disabled>
                Upload
              </Button>
            )}

          </Col>
        </Row>
      </Form>
    </div>
  );
}

export default AddVideo;
